// Canonical TanStack Query key registry per UI spec §8.2. Components and
// hooks consume these — drift here means stale-cache surprises and invalidation
// misses, so the shape is locked by src/queries/keys.test.ts.

import type {
  ListAssetsParams,
  ListCollectionsParams,
  ListGalleryParams,
  ListSessionsParams,
} from '../services';
import type { FavoriteTargetType, LibraryKind, LibraryProduct } from '../domain';

export interface AgentFilter {
  studioId?: string;
  category?: string;
}

export type AssetFilter = ListAssetsParams;
export type CollectionFilter = ListCollectionsParams;

export const queryKeys = {
  labs: ['labs'] as const,
  lab: (id: string) => ['labs', id] as const,

  studios: (labId?: string) => ['studios', { labId: labId ?? null }] as const,
  studio: (id: string) => ['studios', id] as const,

  agents: (filter?: AgentFilter) => ['agents', filter ?? {}] as const,
  agent: (id: string) => ['agents', id] as const,

  tools: () => ['tools'] as const,

  sessions: (params?: ListSessionsParams) => ['sessions', params ?? {}] as const,
  session: (id: string) => ['sessions', id] as const,
  sessionAssets: (id: string) => ['sessions', id, 'assets'] as const,
  history: (sessionId: string) => ['sessions', sessionId, 'history'] as const,

  assets: (filter?: AssetFilter) => ['assets', filter ?? {}] as const,
  asset: (id: string) => ['assets', id] as const,
  folders: () => ['assets', 'folders'] as const,
  gallery: (params?: ListGalleryParams) => ['assets', 'gallery', params ?? {}] as const,
  // Child of asset(id) so invalidating an asset (e.g. after a lifecycle change)
  // also refreshes its family graph.
  assetFamily: (id: string, depth?: number) =>
    ['assets', id, 'family', depth ?? 2] as const,

  // Shared SmartAI library — one bucket per product. Not under 'assets': the
  // Lattice half is another product's data and is refreshed on its own.
  library: (product: LibraryProduct, kind?: LibraryKind) =>
    ['library', product, { kind: kind ?? null }] as const,

  // Path C: collections + favorites (separate top-level buckets).
  collections: (filter?: CollectionFilter) => ['collections', filter ?? {}] as const,
  collection: (id: string) => ['collections', id] as const,
  favorites: (targetType?: FavoriteTargetType) =>
    ['favorites', { targetType: targetType ?? null }] as const,

  providers: () => ['providers'] as const,
  providerHealth: () => ['providers', 'health'] as const,
  providerCatalog: () => ['providers', 'catalog'] as const,
  videoCatalog: () => ['providers', 'video-catalog'] as const,
  ollamaInstalledModels: () => ['providers', 'ollama', 'models'] as const,
  /** BYOK — the caller's own provider keys (statuses only, never values). */
  providerKeys: () => ['providers', 'keys'] as const,
  operationCatalog: () => ['operations', 'catalog'] as const,
  operationStatus: (operationId: string) => ['operations', 'jobs', operationId] as const,
  batchStatus: (batchId: string) => ['operations', 'batch', batchId] as const,

  // Writer's Lab text operations.
  textOperationStatus: (operationId: string) =>
    ['textOperations', 'jobs', operationId] as const,

  // Animation Lab video operations.
  videoOperationStatus: (operationId: string) =>
    ['videoOperations', 'jobs', operationId] as const,

  // Writer's Lab — Storyteller concepts + outlines.
  storytellerConcepts: () => ['storyteller', 'concepts'] as const,
  storytellerConcept: (id: string) => ['storyteller', 'concepts', id] as const,

  // The signed-in user's own profile.
  userProfile: () => ['me', 'profile'] as const,

  // Credits & billing.
  creditBalance: () => ['billing', 'balance'] as const,
  /** The wallet's Naira per dollar for one lab and model (`credits/naira`). */
  nairaRate: (lab: string, model: string) => ['billing', 'naira-rate', lab, model] as const,
  creditPacks: () => ['billing', 'packs'] as const,
  creditLedger: (page: number) => ['billing', 'ledger', page] as const,
};
